import type { Corners, FineWarpPointV2, MediaFitMode, MeshWarp, Point, Surface, SurfaceSourceRegion } from '../types.js';

// =============================================================================
// Calibration v2 — admin/player 공용 warp 렌더러 코어 (PLAN §4.2/§4.3).
// 셰이더 소스 + 지오메트리 빌더 + uniform 빌더. DOM/regl 비의존 순수 코드 —
// 각 패키지가 자기 regl 인스턴스에 이 소스/데이터를 바인딩한다.
//
// v1 대비 제거: split/seam(inHalf) discard, warpSlices, bezierPatch, side 개념.
// v1 대비 추가: mesh 테셀레이션(정점 attribute), 마스크 알파 텍스처(uMask),
// 미디어 텍스처 fit(contain/cover/stretch)를 admin에서도 사용 가능.
//
// homography 계산(computeHomography/invert3x3/toColumnMajor)은 admin warp.ts에
// 있던 것과 동일 구현이 이미 shared '../homography.js'에 있으므로 중복 복사
// 대신 그대로 사용한다 — 패키지 루트(@cornerstone/shared)에서 import.
// (여기서 re-export하면 index.ts의 export * 와 이름이 겹쳐 ambiguous가 된다.)
// =============================================================================

/** v2 절차 패턴 id — split-indicator(v1 전용)는 제외하고 재부여. */
export const PATTERN_ID_V2 = {
  none: 0,
  checker: 1,
  grid: 2,
  crosshair: 3,
  numbered: 4,
  outline: 5,
  'solid-white': 6,
  'solid-gray': 7,
  'solid-black': 8,
} as const;

export type ProceduralNameV2 = keyof typeof PATTERN_ID_V2;

/**
 * 워핑 전 surface 영역의 고정 종횡비. 현재 corners를 사용하면 코너를 움직일
 * 때마다 contain이 재배치되어 텍스처가 surface에 고정되지 않는다.
 */
export function sourceRegionTargetAspect(
  region: SurfaceSourceRegion | undefined,
  canvasAspect: number,
): number {
  const width = region?.width ?? 1;
  const height = region?.height ?? 1;
  return height > 1e-8 ? canvasAspect * (width / height) : canvasAspect;
}

/** fineWarp uniform 슬롯 수 (기존 방식 유지, side 없음). */
export const MAX_FINE_WARP_POINTS_V2 = 8;

// -----------------------------------------------------------------------------
// 셰이더 소스 (WebGL1 / regl 호환)
// -----------------------------------------------------------------------------

/**
 * Vertex — mesh 테셀레이션 지원.
 * aPos = mesh 변위가 적용된 destination-global [0,1] 정점 위치(그대로 클립
 * 공간 매핑), aUV = 변위 전 균등 격자 좌표. 프래그먼트는 aUV(=콘텐츠가 놓일
 * 원래 위치)로 역호모그래피를 돌리므로 GPU 보간이 정점 사이를 bilinear로
 * 매끈하게 이어준다 (map.club과 동일 방식).
 */
export const WARP2_VERT = `
precision highp float;
attribute vec2 aPos;
attribute vec2 aUV;
varying vec2 vUV;
varying vec2 vDest;
void main() {
  vUV = aUV;
  vDest = aPos;
  // [0,1] destination-global (y-down) → clip space (y-up).
  gl_Position = vec4(aPos.x * 2.0 - 1.0, 1.0 - aPos.y * 2.0, 0.0, 1.0);
}
`;

/**
 * Fragment — inverse-homography UV 샘플 + fineWarp 가우시안 delta
 * + 마스크 알파 텍스처 곱 + 절차 패턴 / 미디어 텍스처(contain/cover/stretch).
 */
export const WARP2_FRAG = `
#extension GL_OES_standard_derivatives : enable
precision highp float;

uniform mat3 uHinv;
uniform float uUseMesh;
uniform int uPattern;
uniform float uUseTexture;
uniform sampler2D uTex;
// Float avoids driver/regl integer-uniform coercion differences. Values:
// 0=stretch, 1=contain, 2=cover.
uniform float uFitMode;
uniform float uSourceAspect;
uniform float uTargetAspect;
uniform vec4 uSourceRegion;
uniform float uMediaScale;
uniform vec2 uMediaOffset;
uniform float uGlobalMediaTransform;
// left, top, right, bottom. 내부 surface 경계는 0으로 두어 검은 seam 방지.
uniform vec4 uFadeEdges;
uniform float uHeatmap;
uniform vec2 uResolution;
// 마스크 알파 텍스처 (Surface-local UV로 샘플). uUseMask<0.5면 미적용.
uniform float uUseMask;
uniform sampler2D uMask;
uniform int uFineCount;
uniform vec2 uFineDest0; uniform vec2 uFineDest1; uniform vec2 uFineDest2; uniform vec2 uFineDest3;
uniform vec2 uFineDest4; uniform vec2 uFineDest5; uniform vec2 uFineDest6; uniform vec2 uFineDest7;
uniform vec2 uFineDelta0; uniform vec2 uFineDelta1; uniform vec2 uFineDelta2; uniform vec2 uFineDelta3;
uniform vec2 uFineDelta4; uniform vec2 uFineDelta5; uniform vec2 uFineDelta6; uniform vec2 uFineDelta7;
uniform float uFineRadius0; uniform float uFineRadius1; uniform float uFineRadius2; uniform float uFineRadius3;
uniform float uFineRadius4; uniform float uFineRadius5; uniform float uFineRadius6; uniform float uFineRadius7;

varying vec2 vUV;
varying vec2 vDest;

vec3 patChecker(vec2 uv) {
  vec2 c = floor(uv * 16.0);
  float m = mod(c.x + c.y, 2.0);
  return mix(vec3(0.13), vec3(0.87), m);
}
vec3 patGrid(vec2 uv) {
  vec2 cellSize = vec2(1.0 / 20.0);
  vec2 f = fract(uv / cellSize);
  float line = step(0.92, max(f.x, f.y));
  return mix(vec3(0.08), vec3(1.0, 0.25, 0.25), line);
}
vec3 patCrosshair(vec2 uv) {
  vec2 d = abs(uv - 0.5);
  float cross = step(d.x, 0.003) + step(d.y, 0.003);
  float diag1 = step(abs(uv.x - uv.y), 0.003);
  float diag2 = step(abs(uv.x - (1.0 - uv.y)), 0.003);
  float mark = clamp(cross + diag1 + diag2, 0.0, 1.0);
  return mix(vec3(0.06), vec3(1.0), mark);
}
vec3 patOutline(vec2 uv) {
  float edge = step(min(min(uv.x, uv.y), min(1.0 - uv.x, 1.0 - uv.y)), 0.006);
  return vec3(edge);
}
vec3 patNumbered(vec2 uv) {
  vec2 cellSize = vec2(1.0 / 5.0);
  vec2 f = fract(uv / cellSize);
  float line = step(0.94, max(f.x, f.y));
  vec2 nearest = abs(f - 0.5) * 2.0;
  float dotMark = step(length(nearest - vec2(1.0)), 0.08);
  return mix(vec3(0.05), vec3(1.0, 0.85, 0.25), max(line, dotMark));
}
vec3 samplePattern(vec2 uv, int id) {
  if (id == 1) return patChecker(uv);
  if (id == 2) return patGrid(uv);
  if (id == 3) return patCrosshair(uv);
  if (id == 4) return patNumbered(uv);
  if (id == 5) return patOutline(uv);
  if (id == 6) return vec3(1.0);
  if (id == 7) return vec3(0.5);
  if (id == 8) return vec3(0.0);
  // "none" — neutral gray with a faint checker so the quad stays visible.
  vec2 c = floor(uv * 8.0);
  float m = mod(c.x + c.y, 2.0);
  return mix(vec3(0.18), vec3(0.22), m);
}

vec2 fineWarpDelta(vec2 destUV) {
  vec2 outDelta = vec2(0.0);
  if (uFineCount > 0) { float d = distance(destUV, uFineDest0); float r = max(uFineRadius0, 0.001); outDelta += uFineDelta0 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 1) { float d = distance(destUV, uFineDest1); float r = max(uFineRadius1, 0.001); outDelta += uFineDelta1 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 2) { float d = distance(destUV, uFineDest2); float r = max(uFineRadius2, 0.001); outDelta += uFineDelta2 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 3) { float d = distance(destUV, uFineDest3); float r = max(uFineRadius3, 0.001); outDelta += uFineDelta3 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 4) { float d = distance(destUV, uFineDest4); float r = max(uFineRadius4, 0.001); outDelta += uFineDelta4 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 5) { float d = distance(destUV, uFineDest5); float r = max(uFineRadius5, 0.001); outDelta += uFineDelta5 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 6) { float d = distance(destUV, uFineDest6); float r = max(uFineRadius6, 0.001); outDelta += uFineDelta6 * exp(-4.0 * (d * d) / (r * r)); }
  if (uFineCount > 7) { float d = distance(destUV, uFineDest7); float r = max(uFineRadius7, 0.001); outDelta += uFineDelta7 * exp(-4.0 * (d * d) / (r * r)); }
  return outDelta;
}

void main() {
  vec2 srcUV;
  if (uUseMesh > 0.5) {
    // 메시 지오메트리의 균등 UV를 직접 샘플한다. 역호모그래피를 다시 적용하면
    // 실제 삼각형 경계와 discard 경계가 달라져 셀 단위 계단/잘림이 발생한다.
    srcUV = vUV;
  } else {
    vec3 src = uHinv * vec3(vDest, 1.0);
    if (abs(src.z) < 1e-10) discard;
    srcUV = src.xy / src.z;
  }

  srcUV += fineWarpDelta(vDest);

  if (srcUV.x < 0.0 || srcUV.x > 1.0 || srcUV.y < 0.0 || srcUV.y > 1.0) discard;

  vec3 color;
  if (uUseTexture > 0.5) {
    // schedule은 sourceRegion 뒤의 글로벌 UV를 공통 변환해야 경계가 이어진다.
    // 고정 surface 미디어는 기존처럼 surface 로컬 UV에서 변환한다.
    vec2 baseUV = uGlobalMediaTransform > 0.5
      ? uSourceRegion.xy + srcUV * uSourceRegion.zw
      : srcUV;
    vec2 viewUV = (baseUV - vec2(0.5)) / max(uMediaScale, 1.0) + vec2(0.5) + uMediaOffset;
    vec2 mediaUV = viewUV;
    bool fitVisible = true;
    if (uFitMode > 0.5 && uFitMode < 1.5 && uSourceAspect > 0.0 && uTargetAspect > 0.0) {
      // contain — letterbox/pillarbox, 소스 전체가 보이도록.
      if (uSourceAspect > uTargetAspect) {
        float h = uTargetAspect / uSourceAspect;
        float top = (1.0 - h) * 0.5;
        fitVisible = viewUV.y >= top && viewUV.y <= top + h;
        mediaUV.y = (viewUV.y - top) / h;
      } else {
        float w = uSourceAspect / uTargetAspect;
        float left = (1.0 - w) * 0.5;
        fitVisible = viewUV.x >= left && viewUV.x <= left + w;
        mediaUV.x = (viewUV.x - left) / w;
      }
    } else if (uFitMode >= 1.5 && uSourceAspect > 0.0 && uTargetAspect > 0.0) {
      // cover — 소스를 잘라서 면을 꽉 채움.
      if (uSourceAspect > uTargetAspect) {
        float w = uTargetAspect / uSourceAspect;
        mediaUV.x = (1.0 - w) * 0.5 + viewUV.x * w;
      } else {
        float h = uSourceAspect / uTargetAspect;
        mediaUV.y = (1.0 - h) * 0.5 + viewUV.y * h;
      }
    }
    if (uGlobalMediaTransform < 0.5) {
      mediaUV = uSourceRegion.xy + mediaUV * uSourceRegion.zw;
    }
    fitVisible = fitVisible && mediaUV.x >= 0.0 && mediaUV.x <= 1.0 && mediaUV.y >= 0.0 && mediaUV.y <= 1.0;
    color = fitVisible ? texture2D(uTex, mediaUV).rgb : vec3(0.0);
  } else {
    color = samplePattern(srcUV, uPattern);
  }

  if (uHeatmap > 0.5) {
    float j = abs(dFdx(srcUV.x) * dFdy(srcUV.y) - dFdx(srcUV.y) * dFdy(srcUV.x));
    float baseline = 1.0 / (uResolution.x * uResolution.y);
    float density = baseline / max(j, 1e-10);
    float t = clamp(log2(density) * 0.3 + 0.5, 0.0, 1.0);
    vec3 heat = mix(vec3(0.92, 0.28, 0.2), vec3(0.24, 0.55, 1.0), t);
    color = mix(color, heat, 0.45);
  }

  // Edge antialiasing — 워프된 면 바깥 경계를 소스 1픽셀 폭으로 부드럽게.
  vec2 dx = vec2(dFdx(srcUV.x), dFdy(srcUV.x));
  vec2 dy = vec2(dFdx(srcUV.y), dFdy(srcUV.y));
  float pxX = max(length(dx), 1e-6);
  float pxY = max(length(dy), 1e-6);
  float edgeFade = 1.0;
  if (uFadeEdges.x > 0.5) edgeFade = min(edgeFade, clamp(srcUV.x / pxX, 0.0, 1.0));
  if (uFadeEdges.y > 0.5) edgeFade = min(edgeFade, clamp(srcUV.y / pxY, 0.0, 1.0));
  if (uFadeEdges.z > 0.5) edgeFade = min(edgeFade, clamp((1.0 - srcUV.x) / pxX, 0.0, 1.0));
  if (uFadeEdges.w > 0.5) edgeFade = min(edgeFade, clamp((1.0 - srcUV.y) / pxY, 0.0, 1.0));

  // 마스크는 Surface 로컬 UV로 샘플해 corner/mesh와 함께 워핑한다.
  float maskAlpha = 1.0;
  if (uUseMask > 0.5) maskAlpha = texture2D(uMask, srcUV).a;

  gl_FragColor = vec4(color * edgeFade * maskAlpha, 1.0);
}
`;

// -----------------------------------------------------------------------------
// 지오메트리 — mesh 테셀레이션
// -----------------------------------------------------------------------------

export type SurfaceGeometry = {
  /** 정점 위치 (destination-global [0,1], mesh 변위 적용). x,y interleaved. */
  positions: Float32Array;
  /** 변위 전 균등 격자 좌표 (aUV). x,y interleaved. */
  uvs: Float32Array;
  /** 삼각형 인덱스 (최대 9×9 정점이라 Uint16이면 충분). */
  indices: Uint16Array;
};

/** Surface 로컬 UV를 실제 destination 좌표로 변환한다. */
export function surfaceUvToDest(surface: Pick<Surface, 'corners' | 'mesh'>, uv: Point): Point {
  const u = Math.max(0, Math.min(1, uv.x));
  const v = Math.max(0, Math.min(1, uv.y));
  const mesh = surface.mesh;
  if (mesh && mesh.points.length === (mesh.cols + 1) * (mesh.rows + 1)) {
    const col = Math.min(mesh.cols - 1, Math.floor(u * mesh.cols));
    const row = Math.min(mesh.rows - 1, Math.floor(v * mesh.rows));
    const fu = u * mesh.cols - col;
    const fv = v * mesh.rows - row;
    const stride = mesh.cols + 1;
    const tl = mesh.points[row * stride + col]!;
    const tr = mesh.points[row * stride + col + 1]!;
    const bl = mesh.points[(row + 1) * stride + col]!;
    const br = mesh.points[(row + 1) * stride + col + 1]!;
    return bilinearPoint(tl, tr, br, bl, fu, fv);
  }
  return bilinearPoint(
    surface.corners[0], surface.corners[1], surface.corners[2], surface.corners[3], u, v,
  );
}

/** destination 좌표를 Surface 로컬 UV로 수치 역변환한다. */
export function surfaceDestToUv(surface: Pick<Surface, 'corners' | 'mesh'>, dest: Point): Point {
  let best: Point = { x: 0.5, y: 0.5 };
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let row = 0; row <= 8; row++) {
    for (let col = 0; col <= 8; col++) {
      const candidate = { x: col / 8, y: row / 8 };
      const p = surfaceUvToDest(surface, candidate);
      const distance = (p.x - dest.x) ** 2 + (p.y - dest.y) ** 2;
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
  }
  let u = best.x;
  let v = best.y;
  const epsilon = 1e-4;
  for (let i = 0; i < 12; i++) {
    const p = surfaceUvToDest(surface, { x: u, y: v });
    const pu = surfaceUvToDest(surface, { x: Math.min(1, u + epsilon), y: v });
    const pv = surfaceUvToDest(surface, { x: u, y: Math.min(1, v + epsilon) });
    const j00 = (pu.x - p.x) / epsilon;
    const j10 = (pu.y - p.y) / epsilon;
    const j01 = (pv.x - p.x) / epsilon;
    const j11 = (pv.y - p.y) / epsilon;
    const det = j00 * j11 - j01 * j10;
    if (Math.abs(det) < 1e-10) break;
    const ex = p.x - dest.x;
    const ey = p.y - dest.y;
    u = Math.max(0, Math.min(1, u - (j11 * ex - j01 * ey) / det));
    v = Math.max(0, Math.min(1, v - (-j10 * ex + j00 * ey) / det));
  }
  return { x: u, y: v };
}

function bilinearPoint(tl: Point, tr: Point, br: Point, bl: Point, u: number, v: number): Point {
  const topX = tl.x + (tr.x - tl.x) * u;
  const topY = tl.y + (tr.y - tl.y) * u;
  const bottomX = bl.x + (br.x - bl.x) * u;
  const bottomY = bl.y + (br.y - bl.y) * u;
  return {
    x: topX + (bottomX - topX) * v,
    y: topY + (bottomY - topY) * v,
  };
}

/**
 * 4개 corner로 정의된 Surface를 촘촘한 bilinear 패치로 만든다. 독립
 * homography 대신 동일한 UV 행을 공유하므로 인접 Surface 경계의 텍스처가
 * 같은 위치에서 만나며, 큰 삼각형 대각선 접힘도 눈에 띄지 않는다.
 */
export function buildSurfacePatchGeometry(
  corners: Corners,
  subdivisions = 12,
): SurfaceGeometry {
  const n = Math.max(1, Math.min(32, Math.floor(subdivisions)));
  const [tl, tr, br, bl] = corners;
  const points: Point[] = [];
  for (let r = 0; r <= n; r++) {
    const v = r / n;
    for (let c = 0; c <= n; c++) {
      const u = c / n;
      const topX = tl.x + (tr.x - tl.x) * u;
      const topY = tl.y + (tr.y - tl.y) * u;
      const bottomX = bl.x + (br.x - bl.x) * u;
      const bottomY = bl.y + (br.y - bl.y) * u;
      points.push({
        x: topX + (bottomX - topX) * v,
        y: topY + (bottomY - topY) * v,
      });
    }
  }
  return buildSurfaceGeometry({ cols: n, rows: n, points });
}

/**
 * surface 지오메트리를 만든다. mesh가 null/손상(점 개수 불일치)이면
 * 4정점 quad 1장. 아니면 (cols×rows) 그리드
 * 테셀레이션: 정점 위치는 mesh.points, UV는 균등 격자.
 */
export function buildSurfaceGeometry(mesh: MeshWarp | null): SurfaceGeometry {
  const expected = mesh ? (mesh.cols + 1) * (mesh.rows + 1) : 0;
  if (!mesh || mesh.points.length !== expected) {
    // TL, TR, BR, BL — 두 삼각형.
    const quad = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
    return {
      positions: quad,
      uvs: quad.slice(),
      indices: new Uint16Array([0, 1, 2, 0, 2, 3]),
    };
  }
  const { cols, rows, points } = mesh;
  const vertCount = (cols + 1) * (rows + 1);
  const positions = new Float32Array(vertCount * 2);
  const uvs = new Float32Array(vertCount * 2);
  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c <= cols; c++) {
      const i = r * (cols + 1) + c;
      const p = points[i]!;
      positions[i * 2] = p.x;
      positions[i * 2 + 1] = p.y;
      uvs[i * 2] = c / cols;
      uvs[i * 2 + 1] = r / rows;
    }
  }
  const indices = new Uint16Array(cols * rows * 6);
  let k = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i00 = r * (cols + 1) + c;
      const i10 = i00 + 1;
      const i01 = i00 + (cols + 1);
      const i11 = i01 + 1;
      indices[k++] = i00; indices[k++] = i10; indices[k++] = i11;
      indices[k++] = i00; indices[k++] = i11; indices[k++] = i01;
    }
  }
  return { positions, uvs, indices };
}

// -----------------------------------------------------------------------------
// uniform 빌더
// -----------------------------------------------------------------------------

type Vec2 = [number, number];

/** column-major 3x3(H)를 점 p에 적용 (perspective divide 포함). */
export function applyColumnMajor(H: number[], p: { x: number; y: number }): Vec2 {
  const z = H[2]! * p.x + H[5]! * p.y + H[8]!;
  if (Math.abs(z) < 1e-10) return [p.x, p.y];
  return [
    (H[0]! * p.x + H[3]! * p.y + H[6]!) / z,
    (H[1]! * p.x + H[4]! * p.y + H[7]!) / z,
  ];
}

export type FineWarpUniformsV2 = {
  uFineCount: number;
  uFineDest0: Vec2; uFineDest1: Vec2; uFineDest2: Vec2; uFineDest3: Vec2;
  uFineDest4: Vec2; uFineDest5: Vec2; uFineDest6: Vec2; uFineDest7: Vec2;
  uFineDelta0: Vec2; uFineDelta1: Vec2; uFineDelta2: Vec2; uFineDelta3: Vec2;
  uFineDelta4: Vec2; uFineDelta5: Vec2; uFineDelta6: Vec2; uFineDelta7: Vec2;
  uFineRadius0: number; uFineRadius1: number; uFineRadius2: number; uFineRadius3: number;
  uFineRadius4: number; uFineRadius5: number; uFineRadius6: number; uFineRadius7: number;
};

/**
 * fineWarp uniform 값을 만든다 (v1과 동일 구조, side 없음).
 * Hinv는 column-major inverse homography — 각 점의 delta는
 * "핸들의 source 목표 − 순수 호모그래피가 주는 source"로 계산된다.
 */
export function fineWarpUniformsV2(
  Hinv: number[],
  points: FineWarpPointV2[],
): FineWarpUniformsV2 {
  const selected = points.slice(0, MAX_FINE_WARP_POINTS_V2);
  const dest: Vec2[] = [];
  const delta: Vec2[] = [];
  const radius: number[] = [];
  for (const p of selected) {
    const base = applyColumnMajor(Hinv, p.dest);
    dest.push([p.dest.x, p.dest.y]);
    delta.push([p.source.x - base[0], p.source.y - base[1]]);
    radius.push(p.radius);
  }
  while (dest.length < MAX_FINE_WARP_POINTS_V2) {
    dest.push([0, 0]);
    delta.push([0, 0]);
    radius.push(0.001);
  }
  return {
    uFineCount: selected.length,
    uFineDest0: dest[0]!, uFineDest1: dest[1]!, uFineDest2: dest[2]!, uFineDest3: dest[3]!,
    uFineDest4: dest[4]!, uFineDest5: dest[5]!, uFineDest6: dest[6]!, uFineDest7: dest[7]!,
    uFineDelta0: delta[0]!, uFineDelta1: delta[1]!, uFineDelta2: delta[2]!, uFineDelta3: delta[3]!,
    uFineDelta4: delta[4]!, uFineDelta5: delta[5]!, uFineDelta6: delta[6]!, uFineDelta7: delta[7]!,
    uFineRadius0: radius[0]!, uFineRadius1: radius[1]!, uFineRadius2: radius[2]!, uFineRadius3: radius[3]!,
    uFineRadius4: radius[4]!, uFineRadius5: radius[5]!, uFineRadius6: radius[6]!, uFineRadius7: radius[7]!,
  };
}

/** MediaFitMode → uFitMode 값 (0=stretch, 1=contain, 2=cover). */
export function fitModeToUniform(fit: MediaFitMode | undefined): number {
  return fit === 'contain' ? 1 : fit === 'cover' ? 2 : 0;
}
